import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Calendar, Clock, Users, Zap, ChevronRight, Timer, Flame, Share2, Bookmark, Globe, Tag, MapPin } from 'lucide-react';
import WebinarImage from './WebinarImage';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useGlobalData } from '../contexts/GlobalDataContext';
import enhancedDataService from '../utils/enhancedDataService';
import {
  getGoogleCalendarUrl,
  formatDualTimezone,
  getWhatsAppShareUrl,
  getLinkedInShareUrl
} from '../utils/eventShareUtils';
import {
  normalizePricingType,
  determineEventMode,
  normalizeLocation,
  determineExperienceLevel
} from '../utils/eventNormalizationUtils';

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDate = (dateString) => {
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    });
  } catch { return 'TBD'; }
};

const getEventStatus = (eventTime) => {
  const diff = new Date(eventTime) - new Date();
  if (Math.abs(diff) < 3600000) return 'live';
  if (diff < 0)                  return 'past';
  return 'upcoming';
};

const getTimeRemaining = (eventTime) => {
  const diff = new Date(eventTime) - new Date();
  if (diff <= 0) return 'Started';
  const days  = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins  = Math.floor((diff % 3600000)  / 60000);
  if (days > 0)  return `${days}d ${hours}h left`;
  if (hours > 0) return `${hours}h ${mins}m left`;
  return `${mins}m left`;
};

// ── WebinarCard ───────────────────────────────────────────────────────────────

const WebinarCard = ({ event }) => {
  const { isVerified, showLogin } = useAuth();
  const { savedWebinarIds, toggleSaveWebinar } = useGlobalData();
  const isSaved = savedWebinarIds?.includes(event.id);

  const [timeRemaining, setTimeRemaining] = useState(() => getTimeRemaining(event.event_time));
  const status = getEventStatus(event.event_time);

  const cardRef = useRef(null);
  const tzInfo = formatDualTimezone(event.event_time);

  // Normalized Property Calculations
  const normalizedPrice = normalizePricingType(event.pricing_type);
  const eventMode = event.event_mode || determineEventMode(event.title, event.description, event.location, event.external_event_url);
  const locInfo = normalizeLocation(event.location, event.tags || [], event.description);
  const expLevel = determineExperienceLevel(event.title, event.description, event.tags || []);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          enhancedDataService.trackEvent('webinar_view', { webinar_id: event.id });
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, [event.id]);


  useEffect(() => {
    setTimeRemaining(getTimeRemaining(event.event_time));
    if (status === 'past') return;
    const timer = setInterval(() => setTimeRemaining(getTimeRemaining(event.event_time)), 60000);
    return () => clearInterval(timer);
  }, [event.event_time, status]);

  const handleShare = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const url = event.external_event_url || window.location.href;
    const shareText = `Check out this webinar: "${event.title}" on TestingVala!`;

    if (navigator.share) {
      navigator.share({
        title: 'Share Webinar',
        text: shareText,
        url: url
      }).catch(err => console.warn('Native share failed or cancelled:', err));
    } else {
      navigator.clipboard.writeText(url).then(() => {
        toast.success('Link copied', {
          style: { background: '#161B22', color: '#C9D1D9', border: '1px solid #21262D', fontSize: '12px' },
        });
      });
    }
  }, [event.external_event_url, event.title]);

  const hasValidLink = event.external_event_url && 
    event.external_event_url !== '#' && 
    !event.external_event_url.startsWith('javascript:');

  const cta = !hasValidLink
    ? { text: 'Coming Soon',      icon: null }
    : status === 'live'
    ? { text: 'Watch Live',      icon: <Zap className="w-3.5 h-3.5 fill-current" /> }
    : status === 'past'
    ? { text: 'Watch Recording', icon: <ChevronRight className="w-3.5 h-3.5" /> }
    : { text: 'Reserve Seat',    icon: <ChevronRight className="w-3.5 h-3.5" /> };

  const regCount = event.registrations_count ?? event.registered_count ?? 0;

  return (
    // Slightly lighter surface + softer border + layered shadow depth
    <div ref={cardRef} className="group flex flex-col bg-[#13181F] rounded-xl border border-[#21262D] overflow-hidden hover:border-[#30363D] shadow-sm shadow-black/40 hover:shadow-md hover:shadow-black/50 transition-all duration-200 h-full">

      {/* Thumbnail */}
      <div className="relative aspect-[16/9] overflow-hidden flex-shrink-0">
        <WebinarImage
          src={event.image_url}
          alt={event.title}
          className="group-hover:scale-[1.02] transition-transform duration-500"
        />

        {/* Status badges */}
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          {status === 'live' && (
            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-[#2A1215] text-[#FF7B72] border border-[#492326] uppercase tracking-wide flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF7B72] animate-pulse" /> LIVE
            </span>
          )}
          {event.is_featured && (
            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-[#261C05] text-[#E3B341] border border-[#4D3D11] uppercase tracking-wide flex items-center gap-1">
              <Flame className="w-3 h-3" /> FEATURED
            </span>
          )}

          {/* Mode Badge (Online / Offline / Hybrid) */}
          <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold border uppercase tracking-wide flex items-center gap-1 ${
            eventMode === 'online' ? 'bg-[#0D1117] text-[#58A6FF] border-[#21262D]' :
            eventMode === 'offline' ? 'bg-[#261C05] text-[#E3B341] border-[#4D3D11]' :
            'bg-[#1F192B] text-[#A371F7] border-[#3C2E59]'
          }`}>
            {eventMode === 'online' ? '🟢 Online' : eventMode === 'offline' ? '🏢 Offline' : '🔀 Hybrid'}
          </span>

          {/* Price Badge (Free / Paid) */}
          <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold border uppercase tracking-wide ${
            normalizedPrice === 'Free' ? 'bg-[#0F221B] text-[#56D364] border-[#1B4B27]' :
            normalizedPrice === 'Paid' ? 'bg-[#261C05] text-[#E3B341] border-[#4D3D11]' :
            'bg-[#0D1117] text-[#8B949E] border-[#21262D]'
          }`}>
            {normalizedPrice === 'Free' ? '🆓 Free' : normalizedPrice === 'Paid' ? '💳 Paid' : '❓ Unknown'}
          </span>

          {locInfo.isIndia && (
            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-[#0F221B] text-[#56D364] border border-[#1B4B27] tracking-wide flex items-center gap-1">
              🇮🇳 {locInfo.city || 'India'}
            </span>
          )}
        </div>

        {/* Bookmark button */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isVerified) {
              showLogin('bookmark', () => {
                toggleSaveWebinar(event.id);
                enhancedDataService.trackEvent('webinar_save', { webinar_id: event.id });
              });
            } else {
              toggleSaveWebinar(event.id);
              enhancedDataService.trackEvent('webinar_save', { webinar_id: event.id });
            }
          }}
          aria-label={isSaved ? "Remove bookmark" : "Bookmark event"}
          className={`absolute top-3 right-11 p-1.5 rounded-lg bg-[#0D1117]/70 border border-white/5 transition-all ${
            isSaved ? 'text-[#58A6FF] hover:text-[#58A6FF] opacity-100' : 'text-[#6E7681] hover:text-white hover:bg-[#161B22] opacity-0 group-hover:opacity-100'
          }`}
        >
          <Bookmark className={`w-3.5 h-3.5 ${isSaved ? 'fill-current' : ''}`} />
        </button>

        {/* Share button */}
        <button
          onClick={handleShare}
          aria-label="Copy event link"
          className="absolute top-3 right-3 p-1.5 rounded-lg bg-[#0D1117]/70 border border-white/5 text-[#6E7681] hover:text-white hover:bg-[#161B22] transition-all opacity-0 group-hover:opacity-100"
        >
          <Share2 className="w-3.5 h-3.5" />
        </button>

        {/* Countdown */}
        {status === 'upcoming' && (
          <div className="absolute bottom-3 right-3">
            <div className="px-2 py-1 rounded-md bg-[#0D1117]/80 border border-[#21262D] flex items-center gap-1.5">
              <Timer className="w-3 h-3 text-[#58A6FF]" />
              <span className="text-[10px] font-semibold text-[#C9D1D9]">{timeRemaining}</span>
            </div>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-4 sm:p-5 flex flex-col flex-1">

        {/* Speaker row */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-[#21262D] border border-[#30363D] flex items-center justify-center text-[10px] font-bold text-[#8B949E] flex-shrink-0 overflow-hidden">
              {event.speaker_avatar_url
                ? <img src={event.speaker_avatar_url} alt={event.speaker} className="w-full h-full object-cover" />
                : (event.speaker?.charAt(0) || 'E')}
            </div>
            <div className="min-w-0">
              <p className="text-[12px] font-semibold text-[#C9D1D9] truncate leading-tight">{event.speaker || 'Engineering Expert'}</p>
              <p className="text-[10px] text-[#6E7681] truncate leading-tight mt-0.5">{event.speaker_title || 'Technical Specialist'}</p>
            </div>
          </div>
          {regCount > 0 && (
            <div className="flex items-center gap-1 text-[#484F58] flex-shrink-0">
              <Users className="w-3 h-3" />
              <span className="text-[10px] font-semibold">{regCount}</span>
            </div>
          )}
        </div>

        {/* Title */}
        <h3 className="text-[13px] sm:text-[14px] font-bold text-white mb-1.5 leading-[1.4] group-hover:text-[#58A6FF] transition-colors line-clamp-2">
          {event.title}
        </h3>

        {/* Description */}
        <p className="text-[11px] sm:text-[12px] text-[#6E7681] line-clamp-2 mb-3 leading-[1.5]">
          {event.short_description || event.description || 'Join this technical session to master modern engineering workflows.'}
        </p>

        {/* FEATURE 2: Dual Timezone Display (IST + Local Time) */}
        <div className="mt-auto">
          <div className="py-2 px-2.5 rounded-lg bg-[#0D1117]/80 border border-[#21262D] mb-2.5 space-y-0.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1 text-[#C9D1D9]">
                <Calendar className="w-3 h-3 text-[#58A6FF]" />
                <span className="text-[10px] sm:text-[11px] font-semibold">{formatDate(event.event_time)}</span>
              </div>
              <div className="flex items-center gap-1 text-[#56D364] font-semibold text-[10px] sm:text-[11px]">
                <Clock className="w-3 h-3" />
                <span>🇮🇳 {tzInfo.istText}</span>
              </div>
            </div>
            {!tzInfo.isSameTimezone && tzInfo.localText && (
              <div className="flex items-center justify-end gap-1 text-[9px] text-[#8B949E] pt-0.5 border-t border-[#1C2128]/80">
                <Globe className="w-2.5 h-2.5 text-[#8B949E]" />
                <span>{tzInfo.localText}</span>
              </div>
            )}
          </div>

          {/* Quick Actions Row (Calendar, WhatsApp, LinkedIn) */}
          <div className="grid grid-cols-3 gap-2 mb-2.5">
            <a
              href={getGoogleCalendarUrl(event)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                enhancedDataService.trackEvent('add_to_calendar_click', { webinar_id: event.id });
              }}
              aria-label="Add event to Google Calendar"
              className="min-h-[44px] h-[40px] px-1.5 rounded-lg bg-[#161B22] border border-[#21262D] hover:border-[#30363D] hover:bg-[#1C2128] text-[#C9D1D9] hover:text-white text-[11px] font-medium flex items-center justify-center gap-1 transition-all active:scale-[0.98]"
            >
              <Calendar className="w-4 h-4 text-[#58A6FF] flex-shrink-0" />
              <span className="truncate">Calendar</span>
            </a>

            <a
              href={getWhatsAppShareUrl(event)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                enhancedDataService.trackEvent('whatsapp_share_click', { webinar_id: event.id });
              }}
              aria-label="Share event on WhatsApp"
              className="min-h-[44px] h-[40px] px-1.5 rounded-lg bg-[#161B22] border border-[#21262D] hover:border-[#25D366]/40 hover:bg-[#0F221B] text-[#C9D1D9] hover:text-[#25D366] text-[11px] font-medium flex items-center justify-center gap-1 transition-all active:scale-[0.98]"
            >
              <span className="text-[14px] leading-none flex-shrink-0">💬</span>
              <span className="truncate">WhatsApp</span>
            </a>

            <a
              href={getLinkedInShareUrl(event)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                enhancedDataService.trackEvent('linkedin_share_click', { webinar_id: event.id });
              }}
              aria-label="Share event on LinkedIn"
              className="min-h-[44px] h-[40px] px-1.5 rounded-lg bg-[#161B22] border border-[#21262D] hover:border-[#0A66C2]/40 hover:bg-[#0A1D33] text-[#C9D1D9] hover:text-[#70B5F9] text-[11px] font-medium flex items-center justify-center gap-1 transition-all active:scale-[0.98]"
            >
              <span className="text-[14px] leading-none flex-shrink-0">💼</span>
              <span className="truncate">LinkedIn</span>
            </a>
          </div>

          {/* Primary CTA (Full-Width) */}
          {hasValidLink ? (
            <a
              href={event.external_event_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                enhancedDataService.trackEvent('webinar_click', { webinar_id: event.id });
              }}
              className={`w-full min-h-[44px] h-[44px] rounded-lg text-[12px] font-semibold flex items-center justify-center gap-1.5 transition-colors active:scale-[0.98] ${
                status === 'past'
                  ? 'bg-transparent text-[#6E7681] border border-[#21262D] hover:bg-[#161B22] hover:text-[#C9D1D9]'
                  : status === 'live'
                  ? 'bg-[#FF7B72]/8 text-[#FF7B72] border border-[#FF7B72]/20 hover:bg-[#FF7B72]/15'
                  : 'bg-[#58A6FF] text-[#0D1117] hover:bg-[#79C0FF] font-bold shadow-md shadow-[#58A6FF]/10'
              }`}
            >
              {cta.text} {cta.icon}
            </a>
          ) : (
            <button
              disabled
              className="w-full min-h-[44px] h-[44px] rounded-lg text-[12px] font-semibold flex items-center justify-center gap-1.5 bg-[#161B22]/50 text-[#484F58] border border-[#21262D]/50 cursor-not-allowed opacity-60"
            >
              Coming Soon
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Skeleton ──────────────────────────────────────────────────────────────────

export const WebinarCardSkeleton = () => (
  <div className="bg-[#13181F] rounded-xl border border-[#21262D] overflow-hidden animate-pulse h-full flex flex-col shadow-sm shadow-black/40">
    <div className="aspect-[16/9] bg-[#1C2128]" />
    <div className="p-5 flex-1 space-y-4">
      <div className="flex items-center gap-2.5">
        <div className="w-7 h-7 rounded-full bg-[#21262D]" />
        <div className="flex-1 space-y-1.5">
          <div className="h-2.5 w-24 bg-[#21262D] rounded" />
          <div className="h-2 w-16 bg-[#21262D] rounded" />
        </div>
      </div>
      <div className="space-y-2">
        <div className="h-3.5 w-full bg-[#21262D] rounded" />
        <div className="h-3.5 w-3/4 bg-[#21262D] rounded" />
      </div>
      <div className="space-y-1.5">
        <div className="h-2.5 w-full bg-[#21262D] rounded" />
        <div className="h-2.5 w-5/6 bg-[#21262D] rounded" />
      </div>
      <div className="pt-3 border-t border-[#1C2128] flex justify-between">
        <div className="h-2.5 w-20 bg-[#21262D] rounded" />
        <div className="h-2.5 w-20 bg-[#21262D] rounded" />
      </div>
      <div className="h-9 w-full bg-[#21262D] rounded-lg" />
    </div>
  </div>
);

export default WebinarCard;
